import { useContext, createContext } from 'react';
import { type Reducer, type AnyAction } from 'redux';
import { type TypedUseSelectorHook } from 'react-redux';

import * as hocs from './hocs';
import { TWithDynamicReducerContext } from './hocs/withDynamicReducer/types';

export const createDynamicContext = <TState extends object, TAction extends AnyAction>(
    reducerName: string,
    options: { reducer: (uniqueId: string) => Reducer<TState, TAction>; defaultState?: TState; initialState?: TState }
) => {
    const { reducer, defaultState, initialState } = options;
    const Context = createContext({} as TWithDynamicReducerContext<TState, TAction>);

    const withDynamicReducer = (customReducerData = {}) => {
        return hocs.withDynamicReducer({ reducerName, reducer, defaultState, initialState, Context, ...customReducerData });
    };

    const useDynamicReducerSelector: TypedUseSelectorHook<TState> = (selector, equalityFn) => {
        const { useComponentSelector } = useContext(Context);

        return useComponentSelector(selector, equalityFn);
    };

    const useDynamicReducerDispatch = () => {
        const { componentDispatch } = useContext(Context);

        return componentDispatch;
    };

    return {
        withDynamicReducer,
        useDynamicReducerSelector,
        useDynamicReducerDispatch
    };
};
